/**
 * @license
 * Copyright 2026 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 *
 * g3-prettier-ignore-file
 */

/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { triggersCreate } from "../funcs/triggers-create.js";
import { triggersDelete } from "../funcs/triggers-delete.js";
import { triggersGet } from "../funcs/triggers-get.js";
import { triggersListExecutions } from "../funcs/triggers-list-executions.js";
import { triggersList } from "../funcs/triggers-list.js";
import { triggersRun } from "../funcs/triggers-run.js";
import { triggersUpdate } from "../funcs/triggers-update.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as interactions from "../models/interactions/index.js";
import {
  CreateTriggerParams,
  DeleteTriggerParams,
  GetTriggerParams,
  ListTriggerExecutionsParams,
  ListTriggersParams,
  RunTriggerParams,
  UpdateTriggerParams,
} from "../models/operations/method-params.js";
import * as triggers from "../models/triggers/index.js";
import { APIPromise, unwrapAsAPIPromise } from "../types/async.js";

export class Triggers extends ClientSDK {
  /**
   * Creates a new trigger that will invoke the specified agent on the given cron schedule.
   */
  create(
    params: CreateTriggerParams,
    options?: RequestOptions,
  ): APIPromise<triggers.Trigger> {
    const { api_version, ...body } = params;
    return unwrapAsAPIPromise(triggersCreate(
      this,
      body,
      api_version,
      options,
    ));
  }

  /**
   * Lists triggers for a project.
   */
  list(
    params?: ListTriggersParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<triggers.ListTriggersResponse> {
    return unwrapAsAPIPromise(triggersList(
      this,
      params?.api_version,
      params?.filter,
      params?.page_size,
      params?.page_token,
      options,
    ));
  }

  /**
   * Gets details of a single trigger.
   */
  get(
    id: string,
    params?: GetTriggerParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<triggers.Trigger> {
    return unwrapAsAPIPromise(triggersGet(
      this,
      id,
      params?.api_version,
      options,
    ));
  }

  /**
   * Updates a trigger.
   */
  update(
    id: string,
    params: UpdateTriggerParams,
    options?: RequestOptions,
  ): APIPromise<triggers.Trigger> {
    const { api_version, ...body } = params;
    return unwrapAsAPIPromise(triggersUpdate(
      this,
      id,
      body,
      api_version,
      options,
    ));
  }

  /**
   * Deletes a trigger.
   */
  delete(
    id: string,
    params?: DeleteTriggerParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<interactions.Empty> {
    return unwrapAsAPIPromise(triggersDelete(
      this,
      id,
      params?.api_version,
      options,
    ));
  }

  /**
   * Runs a trigger immediately.
   */
  run(
    trigger_id: string,
    params?: RunTriggerParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<triggers.TriggerExecution> {
    return unwrapAsAPIPromise(triggersRun(
      this,
      trigger_id,
      params?.api_version,
      options,
    ));
  }

  /**
   * Lists executions for a trigger.
   */
  listExecutions(
    trigger_id: string,
    params?: ListTriggerExecutionsParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<triggers.ListTriggerExecutionsResponse> {
    return unwrapAsAPIPromise(triggersListExecutions(
      this,
      trigger_id,
      params?.api_version,
      params?.page_size,
      params?.page_token,
      options,
    ));
  }
}
